import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, Table } from "reactstrap"
import api from "API/Api"
import { usePermissions } from "API/PermissionContext"

const ProducerPerformance = () => {
  const { canExport, MODULE } = usePermissions()
  const MID = MODULE.REPORTS

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState([])
  const [stats, setStats] = useState(null)
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 })
  const [routes, setRoutes] = useState([])
  const [centers, setCenters] = useState([])

  const [from, setFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10))
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10))
  const [route, setRoute] = useState("")
  const [center, setCenter] = useState("")
  const [page, setPage] = useState(1)
  const [sortField, setSortField] = useState("totalQty")
  const [sortAsc, setSortAsc] = useState(false)

  useEffect(() => { loadFilters() }, [])
  useEffect(() => { loadData() }, [from, to, route, center, page])

  const loadFilters = async () => {
    try {
      const [rResp, cResp] = await Promise.all([api.get("routes?per_page=500"), api.get("collection-center-vlcc?per_page=500")])
      const rd = rResp.data?.data; setRoutes(Array.isArray(rd) ? rd : rd?.data || [])
      const cd = cResp.data?.data; setCenters(Array.isArray(cd) ? cd : cd?.data || [])
    } catch (e) { console.error(e) }
  }

  const loadData = async () => {
    setLoading(true)
    try {
      const params = { from, to, page, per_page: 100 }
      if (route) params.route = route
      if (center) params.center = center
      const resp = await api.get("reports/producer-performance", { params })
      if (resp.data.success) {
        const d = resp.data.data
        setData(d?.data || d || [])
        setPagination({ current_page: d?.current_page || 1, last_page: d?.last_page || 1, total: d?.total || 0 })
        setStats(resp.data.stats || null)
      }
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const exportCSV = () => {
    window.open(`${api.defaults.baseURL}/reports/producer-performance?from=${from}&to=${to}&route=${route}&center=${center}&export=csv`, "_blank")
  }

  const handleSort = (field) => {
    if (sortField === field) setSortAsc(!sortAsc)
    else { setSortField(field); setSortAsc(false) }
  }

  const sorted = [...data].sort((a, b) => {
    const av = a[sortField] ?? 0, bv = b[sortField] ?? 0
    return sortAsc ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1)
  })

  const thSort = (label, field) => (
    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort(field)}>
      {label} {sortField === field ? (sortAsc ? "↑" : "↓") : ""}
    </th>
  )

  const cardStyle = (color) => ({
    background: `linear-gradient(135deg, ${color}12, ${color}06)`, borderLeft: `4px solid ${color}`,
    borderRadius: "12px", padding: "18px 20px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  })

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4><i className="fas fa-user-chart me-2" style={{ color: "#6366f1" }}></i>Producer Performance</h4></Col>
          <Col md={6} style={{ textAlign: "right" }}>
            {canExport(MID) && <button className="btn btn-success btn-sm" onClick={exportCSV}><i className="fas fa-file-csv me-1"></i>Export CSV</button>}
          </Col>
        </Row>
      </div>
      <div className="dashContent">

        {/* Filters */}
        <Row className="mb-3" style={{ background: "#f8fafc", padding: "12px", borderRadius: "10px" }}>
          <Col md={2}><label style={{ fontSize: "11px", fontWeight: "600", color: "#64748b" }}>From</label><input type="date" className="form-control form-control-sm" value={from} onChange={e => setFrom(e.target.value)} /></Col>
          <Col md={2}><label style={{ fontSize: "11px", fontWeight: "600", color: "#64748b" }}>To</label><input type="date" className="form-control form-control-sm" value={to} onChange={e => setTo(e.target.value)} /></Col>
          <Col md={3}>
            <label style={{ fontSize: "11px", fontWeight: "600", color: "#64748b" }}>Route</label>
            <select className="form-control form-control-sm" value={route} onChange={e => setRoute(e.target.value)}>
              <option value="">All Routes</option>
              {routes.map((r, i) => <option key={i} value={r.id}>{r.routeName}</option>)}
            </select>
          </Col>
          <Col md={3}>
            <label style={{ fontSize: "11px", fontWeight: "600", color: "#64748b" }}>VLCC Center</label>
            <select className="form-control form-control-sm" value={center} onChange={e => setCenter(e.target.value)}>
              <option value="">All Centers</option>
              {centers.map((c, i) => <option key={i} value={c.id}>{c.centerName}</option>)}
            </select>
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <button className="btn btn-outline-danger btn-sm w-100" onClick={() => { setRoute(""); setCenter(""); }}><i className="fas fa-times me-1"></i>Clear</button>
          </Col>
        </Row>

        {loading ? <div style={{ textAlign: "center", padding: "60px" }}><Spinner color="primary" /></div> : (
          <>
            {/* Stats Cards */}
            {stats && (
              <Row className="mb-4">
                {[
                  { label: "Total Producers", value: stats.totalProducers, icon: "fa-users", color: "#6366f1" },
                  { label: "Total Qty (Ltrs)", value: Number(stats.totalQty).toLocaleString("en-IN", { minimumFractionDigits: 2 }), icon: "fa-tint", color: "#22c55e" },
                  { label: "Total Amount", value: "₹" + Number(stats.totalAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 }), icon: "fa-rupee-sign", color: "#f59e0b" },
                  { label: "Total Incentive", value: "₹" + Number(stats.totalIncentive).toLocaleString("en-IN", { minimumFractionDigits: 2 }), icon: "fa-gift", color: "#8b5cf6" },
                  { label: "Avg FAT", value: stats.avgFat, icon: "fa-flask", color: "#ec4899" },
                  { label: "Top Producer", value: stats.topProducer, icon: "fa-trophy", color: "#f97316", small: true },
                ].map((c, i) => (
                  <Col key={i} md={2} sm={6} className="mb-3">
                    <div style={cardStyle(c.color)}>
                      <div style={{ fontSize: "10px", color: "#6c757d", textTransform: "uppercase", letterSpacing: "1px", fontWeight: "600" }}>{c.label}</div>
                      <div style={{ fontSize: c.small ? "14px" : "20px", fontWeight: "700", color: "#1e293b", marginTop: "4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.value}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}

            {/* Table */}
            {sorted.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px", background: "#fff", borderRadius: "12px" }}>
                <i className="fas fa-inbox" style={{ fontSize: "48px", color: "#cbd5e1", marginBottom: "12px" }}></i>
                <p style={{ color: "#94a3b8" }}>No producer data found for this period</p>
              </div>
            ) : (
              <div style={{ background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                <div className="table-responsive">
                  <Table className="table mb-0" size="sm">
                    <thead>
                      <tr style={{ background: "#f8fafc", fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        <th>#</th>
                        <th>No.</th>
                        <th>Producer</th>
                        {thSort("Entries", "entries")}
                        {thSort("Qty (Ltrs)", "totalQty")}
                        {thSort("Avg FAT", "avgFat")}
                        {thSort("Avg SNF", "avgSnf")}
                        {thSort("Avg Rate", "avgRate")}
                        {thSort("Amount", "totalAmount")}
                        {thSort("Incentive", "totalIncentive")}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((d, i) => (
                        <tr key={d.producer_id || i} style={{ fontSize: "13px", borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ color: "#94a3b8" }}>{(page - 1) * 100 + i + 1}</td>
                          <td><span style={{ background: "#e0e7ff", color: "#4338ca", padding: "2px 8px", borderRadius: "8px", fontSize: "11px", fontWeight: "600" }}>{d.producerNumber}</span></td>
                          <td style={{ fontWeight: "600", color: "#1e293b", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.producerName}</td>
                          <td>{d.entries}</td>
                          <td style={{ fontWeight: "600", color: "#0284c7" }}>{Number(d.totalQty).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td><span style={{ background: "#f0fdf4", color: "#166534", padding: "2px 8px", borderRadius: "8px", fontSize: "12px" }}>{Number(d.avgFat).toFixed(2)}</span></td>
                          <td><span style={{ background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: "8px", fontSize: "12px" }}>{Number(d.avgSnf).toFixed(2)}</span></td>
                          <td>₹{Number(d.avgRate).toFixed(2)}</td>
                          <td style={{ fontWeight: "700", color: "#059669" }}>₹{Number(d.totalAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td style={{ color: "#8b5cf6" }}>₹{Number(d.totalIncentive).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {pagination.last_page > 1 && (
                  <div style={{ padding: "12px 16px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "13px", color: "#6c757d" }}>Page {pagination.current_page} of {pagination.last_page} ({pagination.total} producers)</span>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button className="btn btn-sm btn-outline-primary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                      <button className="btn btn-sm btn-outline-primary" disabled={page >= pagination.last_page} onClick={() => setPage(p => p + 1)}>Next →</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ProducerPerformance
